import { useEffect, useState } from 'react';
import { Disclosure } from '@headlessui/react';
import {
  MinusSmallIcon,
  PlusSmallIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

import { DoubleRangeSlider } from '@fagmobler/ui';

import { useConnector } from 'react-instantsearch-hooks-web';
import connectRange from 'instantsearch.js/es/connectors/range/connectRange';

import type {
  RangeConnectorParams,
  RangeWidgetDescription,
} from 'instantsearch.js/es/connectors/range/connectRange';
import clsx from 'clsx';

const MAX_PRICE = 100000;

export type UseRangeSliderProps = RangeConnectorParams;

export function useRangeSlider(props?: UseRangeSliderProps) {
  return useConnector<RangeConnectorParams, RangeWidgetDescription>(
    connectRange,
    props
  );
}

export const PriceFilter = () => {
  const { start, range, refine, canRefine } = useRangeSlider({
    attribute: 'price',
  });

  const currentRefinement = {
    min: start[0] !== -Infinity ? start[0] : range.min,
    max: start[1] !== Infinity ? start[1] : range.max,
  };

  const min = range.min;
  const max = range.max;

  const [stateMinValue, setStateMinValue] = useState<number | undefined>(min);
  const [stateMaxValue, setStateMaxValue] = useState<number | undefined>(max);

  useEffect(() => {
    if (canRefine) {
      setStateMinValue(currentRefinement.min);
      setStateMaxValue(currentRefinement.max);
    }
  }, [currentRefinement.min, currentRefinement.max, canRefine]);

  if (
    typeof stateMinValue === 'undefined' ||
    typeof stateMaxValue === 'undefined'
  ) {
    return null;
  }

  const onChange = (value: number[]) => {
    setStateMinValue(value[0]);
    setStateMaxValue(value[1]);
  };

  const onChangeCommitted = (value: (number | undefined)[]) => {
    const min = value[0];
    const max = value[1];
    if (
      typeof value[0] !== 'number' ||
      (typeof min !== 'undefined' && value[0] < min)
    ) {
      value[0] = min;
      setStateMinValue(min);
    }

    if (
      typeof value[1] !== 'number' ||
      (typeof max !== 'undefined' && value[1] > max)
    ) {
      value[1] = max;
      setStateMaxValue(max);
    }

    if (
      currentRefinement.min !== value[0] ||
      currentRefinement.max !== value[1]
    ) {
      refine([value[0], value[1]]);
    }
  };

  const isActive = range.min !== start[0] && range.max !== start[1];

  return (
    <Disclosure
      as="div"
      className="border-b border-neutral-40 py-6"
      defaultOpen={isActive}
    >
      {({ open }) => (
        <>
          <h3
            className={clsx('-my-3 flow-root hd-md', {
              'opacity-25': !canRefine,
            })}
          >
            <Disclosure.Button className="py-5 w-full flex items-center justify-between">
              <span>Pris</span>
              <span className="ml-6 flex items-center">
                {open ? (
                  <MinusSmallIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <PlusSmallIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </span>
            </Disclosure.Button>
          </h3>
          <Disclosure.Panel className="pt-6">
            <div className={clsx({ 'opacity-25': !canRefine })}>
              <div className="flex justify-between items-center mb-3">
                <div className="bg-neutral-30 flex w-full p-3 items-center focus-within:outline rounded-sm outline-text-primary outline-2">
                  <input
                    type="text"
                    className="w-full bg-neutral-30  outline-none"
                    value={stateMinValue}
                    disabled={!canRefine}
                    name="minPrice"
                    onChange={(event) =>
                      onChangeCommitted([
                        parseInt(event.target.value),
                        undefined,
                      ])
                    }
                  />
                  <span className="font-medium">Kr</span>
                </div>
                <div>
                  <ArrowRightIcon className="h-5 w-5 mx-3" />
                </div>
                <div className="bg-neutral-30 flex w-full p-3 items-center focus-within:outline rounded-sm outline-text-primary outline-2">
                  <input
                    type="text"
                    className="bg-neutral-30 w-full outline-none"
                    disabled={!canRefine}
                    value={stateMaxValue}
                    name="maxPrice"
                    onChange={(event) =>
                      onChangeCommitted([
                        undefined,
                        parseInt(event.target.value),
                      ])
                    }
                  />
                  <span className="font-medium">Kr</span>
                </div>
              </div>
              <DoubleRangeSlider
                minValue={min || 0}
                maxValue={max || MAX_PRICE}
                value={[stateMinValue, stateMaxValue]}
                disabled={!canRefine}
                onValueCommit={onChangeCommitted}
                onValueChange={(newValue: number[]) => {
                  onChange(newValue);
                }}
              />
              <div
                className="mt-0.5 flex justify-between text-xs font-semibold"
                aria-hidden
              >
                <span>{min}</span>
                <span>{max}</span>
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default PriceFilter;
